import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { CleaningTeam } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

/**
 * "Needs cleaning" asks who is doing it, in the same breath (CAP-419).
 *
 * Answering yes used to clear the question and nothing else: the row left the
 * board, and the job sat unconfirmed with nobody on it until the next
 * reconcile - up to a day, with the crew hearing nothing. A cleaning that
 * needs doing and has nobody doing it is a problem, not a state worth having.
 *
 * So the yes comes through here, with the property's own default preselected,
 * and lands as ONE call: decided, confirmed, assigned. Clearing the picker is
 * still allowed - "it needs a cleaning, crew to follow" is a real answer, and
 * derivation applies the default afterwards - it just is not what happens by
 * accident.
 *
 * **"Not required" never opens this.** Nothing needs choosing to say a cleaning
 * is not happening, and making that two taps would be worse than the problem.
 */

interface DecideDialogProps {
  propertyId: string
  propertyName: string
  /** The day the cleaning would happen, already spelled for a human. */
  day: string
  teams: CleaningTeam[]
  busy: boolean
  onCancel: () => void
  onConfirm: (teamId: string | null) => void
}

export function CleaningDecideDialog({
  propertyId,
  propertyName,
  day,
  teams,
  busy,
  onCancel,
  onConfirm,
}: DecideDialogProps) {
  // Fetched when the dialog opens rather than for every row on the board: the
  // default crew is a per-property setting and only matters once someone is
  // actually answering this question.
  const settingsQuery = useQuery({
    queryKey: ['property', 'cleaning-settings', propertyId],
    queryFn: () => api.getPropertyCleaning(propertyId),
  })

  const defaultTeamId = settingsQuery.data?.settings.default_team_id ?? ''

  /**
   * The operator's pick, or null for "whatever the property says".
   *
   * Derived rather than seeded by an effect, so the default can arrive after
   * the dialog opens without overwriting a choice already made. An explicit
   * empty string is a real answer - "crew to follow" - and is why this is not
   * simply `choice || defaultTeamId`.
   */
  const [choice, setChoice] = useState<string | null>(null)
  const teamId = choice ?? defaultTeamId

  const assignable = teams.filter(team => team.active)
  const knownDefault = assignable.some(team => team.id === defaultTeamId)

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-left">Who is cleaning {propertyName}?</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-textMuted">
            {day}. Confirming this commits the crew, so they can be told.
          </p>

          <div className="space-y-1.5">
            <label
              className="block text-xs uppercase tracking-wider text-textFaint"
              htmlFor="decide-crew"
            >
              Crew
            </label>
            <select
              id="decide-crew"
              value={teamId}
              disabled={busy || settingsQuery.isLoading}
              onChange={event => setChoice(event.target.value)}
              className="h-9 w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50"
            >
              <option value="">Decide later</option>
              {assignable.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                  {team.id === defaultTeamId ? ' (default here)' : ''}
                </option>
              ))}
            </select>
            {/* Said out loud rather than silently falling back to nobody: a
                property whose default crew has been retired, or which never
                had one, is exactly when an operator needs to notice. */}
            {!settingsQuery.isLoading && defaultTeamId && !knownDefault && (
              <p className="text-xs text-amber">
                This property&apos;s default crew is not one that can be assigned any more. Pick
                somebody.
              </p>
            )}
            {!settingsQuery.isLoading && !defaultTeamId && (
              <p className="text-xs text-textFaint">
                This property has no default crew, so there is nobody to fall back on.
              </p>
            )}
            {settingsQuery.isError && (
              <p className="text-xs text-amber">
                The property&apos;s default crew could not be read, so nothing is preselected.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => onConfirm(teamId || null)}>
              {teamId ? 'Needs cleaning' : 'Needs cleaning, crew later'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
